<template>
  <img
    v-if="onlyImg"
    :src="failure ? getImageFailUrl(defaultParameters) : getImageUrl(bestUuid, bestWidth, bestHeight, crop, ratio)"
    :alt="alt"
    :class="imgClass"
    :draggable="draggable"
    @error="failure = true"
    @load="show = true"
  >
  <picture v-else :class="pictureClass">
    <source
      v-for="(properties, px) in orderedMedia"
      :key="px.toString()"
      :srcset="isLazyFlickity ? undefined : sourceOrderedMediaSrc(properties)"
      :data-flickity-lazyload-srcset="isLazyFlickity ? sourceOrderedMediaSrc(properties) : undefined"
      :type="mimetype"
      :media="`(min-width: ${px}px)`"
    >
    <source
      :srcset="isLazyFlickity ? undefined : sourceSrc"
      :data-flickity-lazyload-srcset="isLazyFlickity ? sourceSrc : undefined"
      :type="mimetype"
    >
    <img
      :src="isLazyFlickity ? undefined : imgSrc"
      :alt="alt"
      :class="imgClass"
      :draggable="draggable"
      :data-flickity-lazyload="isLazyFlickity ? imgSrc : undefined"
      :loading="lazy ? 'lazy' : 'eager'"
      @error="failure = true"
      @load="show = true"
    >
  </picture>
</template>

<script>
import { values } from 'lodash-es';
import { getImageUrl as toolGetImageUrl } from '@tools/media-image';

/**
 * Any change should be made on twig Image component too: templates/components/media-image.html.twig
 */
export default {
  props: {
    uuid: { type: String, required: true },
    pictureClass: { type: String, default: '' },
    imgClass: { type: String, default: '' },
    density: { type: Number, default: 2 },
    format: { type: String, default: 'webp' },
    width: { type: Number, default: null },
    height: { type: Number, default: null },
    crop: { type: Array, default: null },
    ratio: { type: Array, default: null },
    alt: { type: String, default: '' },
    onlyImg: { type: Boolean, default: false },
    draggable: { type: Boolean, default: true },
    media: {
      type: Object,
      default: () => {
      },
    },
    isLazyFlickity: { type: Boolean, default: false },
    lazy: { type: Boolean, default: false },
  },
  data() {
    return {
      formatsMimetypes: window.IMAGE_FORMATS_MIMETYPES,
      failure: false,
      show: false,
    };
  },
  computed: {
    defaultParameters() {
      return {
        uuid: this.uuid,
        width: this.width,
        height: this.height,
        crop: this.crop,
        ratio: this.ratio,
      };
    },
    bestUuid() {
      let bestUuid = this.uuid;
      values(this.media).forEach((properties) => {
        if (properties.uuid && this.isMediaSizeBetterThanDefault(properties)) {
          bestUuid = properties.uuid;
        }
      });
      return bestUuid;
    },
    bestWidth() {
      let bestWidth = this.width;
      values(this.media).forEach((properties) => {
        if (properties.width && this.isMediaSizeBetterThanDefault(properties)) {
          bestWidth = properties.width;
        }
      });
      return bestWidth;
    },
    bestHeight() {
      let bestHeight = this.height;
      values(this.media).forEach((properties) => {
        if (properties.height && this.isMediaSizeBetterThanDefault(properties)) {
          bestHeight = properties.height;
        }
      });
      return bestHeight;
    },
    orderedMedia() {
      if (!this.media) {
        return null;
      }
      return Object.keys(this.media).sort().reverse().reduce((object, key) => {
        const newObject = { ...object };
        newObject[key] = this.media[key];
        return newObject;
      }, {});
    },
    mimetype() {
      return this.formatsMimetypes[this.format] || 'application/octet-stream';
    },
    imgSrc() {
      return this.failure ? this.getImageFailUrl(this.defaultParameters) : this.getImageUrl(this.bestUuid, this.bestWidth, this.bestHeight, this.crop, this.ratio);
    },
    sourceSrc() {
      return this.failure ? this.getImageFailUrl(this.defaultParameters) : this.getSrcSet(this.uuid, this.width, this.height, this.crop, this.ratio);
    },
  },
  methods: {
    getImageUrl(uuid, width, height, crop, ratio) {
      return toolGetImageUrl(uuid, this.format, width, height, crop, ratio, window.IMAGE_ORIGINAL_OPTIMIZED);
    },
    isMediaSizeBetterThanDefault(properties) {
      return (this.width && properties.width && properties.width > this.width)
        || (this.height && properties.height && properties.height > this.height);
    },
    getSrcSet(uuid, width, height, crop, ratio) {
      let srcSet = '';
      for (let d = 1; d <= this.density; d++) {
        if (d !== 1) {
          srcSet += ',';
        }
        // "undefined" = keep default setup value, "null" = keep original image value, no nested ternary
        let paramWidth;
        if (width === undefined) {
          paramWidth = this.width;
        } else {
          paramWidth = width ? width * d : null;
        }
        let paramHeight;
        if (height === undefined) {
          paramHeight = this.height;
        } else {
          paramHeight = height ? height * d : null;
        }
        let paramCrop;
        if (crop === undefined) {
          paramCrop = this.crop;
        } else {
          paramCrop = crop ? crop.map((v) => v * d) : null;
        }
        let paramRatio;
        if (ratio === undefined) {
          paramRatio = this.ratio;
        } else {
          paramRatio = ratio;
        }
        srcSet += this.getImageUrl(
          uuid || this.uuid,
          paramWidth,
          paramHeight,
          paramCrop,
          paramRatio,
        );
        srcSet += ` ${d}x`;
      }
      return srcSet;
    },
    getImageFailUrl(parameters) {
      // "undefined" = keep default setup value, "null" = keep original image value
      const requestParameters = {
        format: this.format,
        density: this.density,
      };

      [ 'width', 'height', 'crop', 'ratio' ].forEach((key) => {
        const value = parameters[key] !== undefined ? parameters[key] : this.$props[key];
        if (value) {
          requestParameters[key] = value;
        }
      });
      return `/image/${parameters.uuid ?? this.uuid}?${new URLSearchParams(requestParameters)}`;
    },
    sourceOrderedMediaSrc(properties) {
      return this.failure
        ? this.getImageFailUrl({
          uuid: properties.uuid,
          width: properties.width,
          height: properties.height,
          crop: properties.crop,
          ratio: properties.ratio,
        })
        : this.getSrcSet(properties.uuid, properties.width, properties.height, properties.crop, properties.ratio);
    },
  },
};
</script>
